import { useState, useCallback, useEffect } from 'react';
import type { PluginSDK, WorkflowResult } from './types';

/**
 * Hook for executing CRUD commands with React Query
 * @example const { data, isLoading } = useCrudCommand(sdk, 'av_openldap', 'account', 'get_all_users')
 */
export function useCrudCommand(
	sdk: PluginSDK,
	system: string,
	objectType: string,
	action: string,
	params?: Record<string, any>,
	options?: any
) {
	return sdk.crud.useCommand(system, objectType, action, params, options);
}

/**
 * Hook for workflow execution with state management
 * @example const { execute, loading, error } = useCrudWorkflow(sdk, 'create_user')
 */
export function useCrudWorkflow(sdk: PluginSDK, workflowName: string) {
	const [state, setState] = useState<{
		loading: boolean;
		error: Error | null;
		result: any;
	}>({
		loading: false,
		error: null,
		result: null
	});
	
	const execute = useCallback(async (data: Record<string, any>) => {
		setState({ loading: true, error: null, result: null });
		try {
			const result = await sdk.crud.executeWorkflow(workflowName, data);
			setState({ loading: false, error: null, result });
			return result;
		} catch (error) {
			const err = error instanceof Error ? error : new Error('Workflow execution failed');
			setState({ loading: false, error: err, result: null });
			throw err;
		}
	}, [sdk, workflowName]);
	
	return { execute, ...state };
}

/**
 * Hook for monitoring workflow status via SSE
 * @example const status = useWorkflowStatus(sdk, executionId)
 */
export function useWorkflowStatus(sdk: PluginSDK, executionId: string | null) {
	const [status, setStatus] = useState<any>(null);
	
	useEffect(() => {
		if (!executionId) return;
		
		const unsubscribe = sdk.crud.subscribeWorkflowStatus(executionId, setStatus);
		return unsubscribe;
	}, [sdk, executionId]);
	
	return status;
}

/**
 * Hook for browsing CRUD catalog
 * @example const { data: catalog } = useCrudCatalog(sdk)
 */
export function useCrudCatalog(sdk: PluginSDK) {
	return sdk.api.useQuery(
		['crud-catalog'],
		() => sdk.crud.browseCatalog()
	);
}

/**
 * Hook for getting form schema
 * @example const { data: schema } = useFormSchema(sdk, 'av_openldap', 'account', 'create_user')
 */
export function useFormSchema(
	sdk: PluginSDK,
	system: string,
	objectType: string,
	action: string
) {
	return sdk.api.useQuery(
		['form-schema', system, objectType, action],
		() => sdk.crud.getFormSchema(system, objectType, action)
	);
}

